import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Cake, Home, Calendar, Baby, Plane, Crown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface FamilyEventTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  default_options: any[];
  is_premium: boolean;
}

const categoryIcons = {
  birthday: Cake,
  holiday: Home,
  dinner: Home,
  vacation: Plane,
  kids: Baby
};

const categoryColors = {
  birthday: 'bg-pink-500/10 text-pink-500',
  holiday: 'bg-green-500/10 text-green-500',
  dinner: 'bg-orange-500/10 text-orange-500',
  vacation: 'bg-blue-500/10 text-blue-500',
  kids: 'bg-purple-500/10 text-purple-500'
};

export const FamilyEventTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<FamilyEventTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<FamilyEventTemplate | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('family_event_templates')
        .select('*')
        .order('category', { ascending: true });

      if (error) throw error;
      setTemplates((data || []).map(item => ({
        ...item,
        default_options: Array.isArray(item.default_options) ? item.default_options : []
      })));
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load event templates');
    } finally {
      setLoading(false);
    }
  };

  const useTemplate = (template: FamilyEventTemplate) => {
    // Navigate to create event page with template data
    const templateData = {
      name: template.name,
      description: template.description,
      options: template.default_options,
      category: template.category
    };
    
    navigate('/create-event', { 
      state: { template: templateData } 
    });
  };

  const previewTemplate = (template: FamilyEventTemplate) => {
    setSelectedTemplate(template);
    setPreviewDialogOpen(true);
  };

  if (loading) {
    return <div className="text-center py-8">Loading templates...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground mb-2">Family Event Templates</h2>
        <p className="text-muted-foreground">
          Get started quickly with pre-designed templates for common family events
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
        {templates.map((template) => {
          const IconComponent = categoryIcons[template.category as keyof typeof categoryIcons] || Calendar;
          const colorClass = categoryColors[template.category as keyof typeof categoryColors] || 'bg-gray-500/10 text-gray-500';
          
          return (
            <Card key={template.id} className="relative hover:shadow-md transition-shadow group h-full flex flex-col">
              {template.is_premium && (
                <Badge className="absolute top-2 right-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-yellow-900 border-0 text-xs z-10">
                  <Crown className="h-3 w-3 mr-1" />
                  Pro
                </Badge>
              )}
              
              <CardHeader className="pb-2 flex-shrink-0">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg ${colorClass} flex items-center justify-center`}>
                    <IconComponent className="h-4 w-4" />
                  </div>
                  <Badge variant="secondary" className="text-xs capitalize">
                    {template.category}
                  </Badge>
                </div>
                <CardTitle className="text-sm leading-tight">{template.name}</CardTitle>
                <CardDescription className="text-xs leading-tight line-clamp-2">{template.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="pt-0 pb-3 flex-grow flex flex-col justify-between">
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      {template.default_options.length} options
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {template.default_options.slice(0, 1).map((option, index) => (
                        <Badge key={index} variant="outline" className="text-xs px-1.5 py-0">
                          {option.value}
                        </Badge>
                      ))}
                      {template.default_options.length > 1 && (
                        <Badge variant="outline" className="text-xs px-1.5 py-0">
                          +{template.default_options.length - 1}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-1 mt-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => previewTemplate(template)}
                    className="flex-1 text-xs h-7"
                  >
                    Preview
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => useTemplate(template)}
                    className="flex-1 text-xs h-7 bg-primary hover:bg-primary/90"
                  >
                    Use
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedTemplate?.name}</DialogTitle>
            <DialogDescription>{selectedTemplate?.description}</DialogDescription>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Category</h4>
                <Badge variant="outline" className="capitalize">
                  {selectedTemplate.category}
                </Badge>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Default Options</h4>
                <div className="grid grid-cols-2 gap-2">
                  {selectedTemplate.default_options.map((option, index) => (
                    <div key={index} className="p-2 border rounded-lg">
                      <span className="text-xs text-muted-foreground capitalize">
                        {option.type}:
                      </span>
                      <p className="font-medium">{option.value}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {selectedTemplate.is_premium && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="h-4 w-4 text-yellow-600" />
                    <span className="font-medium text-yellow-800">Premium Template</span>
                  </div>
                  <p className="text-sm text-yellow-700">
                    This template includes premium features and is available with Family or Pro plans.
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Close
            </Button>
            {selectedTemplate && (
              <Button onClick={() => {
                useTemplate(selectedTemplate);
                setPreviewDialogOpen(false);
              }}>
                Use This Template
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};